/**
 * Persistence layer for alpha access and pack grants (ADR-099, migration 031).
 * All Supabase access for app_access, pack_grants and their RPCs lives here.
 */
import { supabase } from '@/utils/supabase'
import type { PostgrestError } from '@supabase/supabase-js'

export type AccessRole = 'alpha' | 'maintainer'

export interface PackGrantRow {
  code: string
  pack_id: string
  created_at: string
  redeemed_at: string | null
  redeemed_email: string | null
}

/** Redeem a waitlist invitation or pack code. Returns true when the token was valid. */
export async function redeemInvite(token: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('redeem_invite', { p_token: token })
  if (error) {
    console.warn('[access] redeem_invite failed', error.message)
    return false
  }
  return data === true
}

/** The caller's access role and the pack ids it holds a grant for (RLS returns own rows only). */
export async function getMyAccess(): Promise<{ role: AccessRole | null; packs: string[] }> {
  const [access, grants] = await Promise.all([
    supabase.from('app_access').select('role').maybeSingle(),
    supabase.from('pack_grants').select('pack_id'),
  ])
  if (access.error) console.warn('[access] app_access read failed', access.error.message)
  if (grants.error) console.warn('[access] pack_grants read failed', grants.error.message)
  return {
    role: access.error ? null : ((access.data?.role as AccessRole | null) ?? null),
    packs: grants.error ? [] : (grants.data ?? []).map((row) => row.pack_id as string),
  }
}

/** Issue a new unused code for a pack (maintainer only, enforced by the RPC). */
export async function issuePackCode(
  packId: string,
): Promise<{ data: string | null; error: PostgrestError | null }> {
  const { data, error } = await supabase.rpc('issue_pack_code', { p_pack_id: packId })
  return { data: (data as string | null) ?? null, error }
}

/** Revoke a grant by its code (maintainer only, enforced by the RPC). */
export async function revokePackGrant(
  code: string,
): Promise<{ error: PostgrestError | null }> {
  const { error } = await supabase.rpc('revoke_pack_grant', { p_code: code })
  return { error }
}

/** Every pack grant, newest first (maintainer only, enforced by the RPC). */
export async function listPackGrants(): Promise<{
  data: PackGrantRow[] | null
  error: PostgrestError | null
}> {
  const { data, error } = await supabase.rpc('list_pack_grants')
  return { data: (data as PackGrantRow[] | null) ?? null, error }
}
